import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';
import type { FilterState, SortOption } from '@/utils/searchParamsEncoding';

export interface SavedSearch {
  id: string;
  user_id: string;
  name: string;
  query: string | null;
  filters: FilterState;
  sort_by: SortOption;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
  use_count: number;
}

interface CreateSavedSearchParams {
  name: string;
  query: string;
  filters: FilterState;
  sortBy: SortOption;
}

const QUERY_KEY = 'saved-searches';

/**
 * Hook for managing saved searches with React Query
 */
export function useSavedSearches() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all saved searches for the current user
  const {
    data: savedSearches = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEY, user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('saved_searches')
        .select('*')
        .eq('user_id', user.id)
        .order('last_used_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) {
        // If table doesn't exist yet, return empty array
        if (error.code === '42P01') {
          logger.warn('saved_searches table not found - migration needed');
          return [];
        }
        throw error;
      }

      return data as SavedSearch[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create a new saved search
  const createMutation = useMutation({
    mutationFn: async ({ name, query, filters, sortBy }: CreateSavedSearchParams) => {
      if (!user?.id) throw new Error('Must be logged in to save searches');

      const { data, error } = await supabase
        .from('saved_searches')
        .insert({
          user_id: user.id,
          name,
          query: query || null,
          filters,
          sort_by: sortBy,
        })
        .select()
        .single();

      if (error) throw error;
      return data as SavedSearch;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, user?.id] });
    },
    onError: (error) => {
      logger.error('Failed to save search:', error);
    },
  });

  // Delete a saved search
  const deleteMutation = useMutation({
    mutationFn: async (searchId: string) => {
      if (!user?.id) throw new Error('Must be logged in');

      const { error } = await supabase
        .from('saved_searches')
        .delete()
        .eq('id', searchId)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, user?.id] });
    },
    onError: (error) => {
      logger.error('Failed to delete saved search:', error);
    },
  });

  // Record usage of a saved search (updates last_used_at and use_count)
  const recordUseMutation = useMutation({
    mutationFn: async (searchId: string) => {
      if (!user?.id) return;

      const { error } = await supabase
        .from('saved_searches')
        .update({
          last_used_at: new Date().toISOString(),
          use_count: supabase.rpc ? undefined : undefined, // TODO: use RPC for increment
        })
        .eq('id', searchId)
        .eq('user_id', user.id);

      // Also increment use_count
      await supabase.rpc('increment_saved_search_use_count', { search_id: searchId }).catch(() => {
        // RPC might not exist, ignore
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, user?.id] });
    },
  });

  // Update a saved search name
  const updateNameMutation = useMutation({
    mutationFn: async ({ searchId, name }: { searchId: string; name: string }) => {
      if (!user?.id) throw new Error('Must be logged in');

      const { error } = await supabase
        .from('saved_searches')
        .update({ name })
        .eq('id', searchId)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, user?.id] });
    },
  });

  return {
    // Data
    savedSearches,
    isLoading,
    error,

    // Actions
    saveSearch: createMutation.mutateAsync,
    deleteSearch: deleteMutation.mutateAsync,
    recordUse: recordUseMutation.mutate,
    updateName: updateNameMutation.mutateAsync,

    // Mutation states
    isSaving: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
    saveError: createMutation.error,

    // Refetch
    refetch,
  };
}
